/**
 * @fileoverview Own shared document JSON snapshots within one authenticated Redux session.
 *
 * `useDocumentData.js` exposes subscriptions; mutations invalidate affected metadata.
 * Session/revision boundaries prevent older responses from replacing newer identity
 * or mutation state. `kc-internal-frontend/src/AuthContext.js` owns identity
 * transitions and initiates resets; document permission errors also clear cached
 * state. A denial in another workspace must not reset document subscriptions.
 *
 * Binary bytes, object URLs, and editor/iframe state belong to consuming workspaces.
 * A cached response preserves server data without becoming an authorization source.
 * Subscription lifetime can exceed route visibility, while account identity must
 * never cross the cache boundary.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createSlice } from '@reduxjs/toolkit';
import api from '../../api';

// Returning after a long editing session must never become a cold navigation.
// RTK Query explicitly supports Infinity; logout/account changes clear it.
export const DOCUMENT_CACHE_SECONDS = Infinity;
export const DOCUMENT_FRESH_SECONDS = 60;

// Only JSON metadata belongs here. File bodies, object URLs and iframe state
// remain outside Redux. Query order must not create duplicate cache entries.


export function documentResourceUrl(url) {
  const [path, search = ''] = url.split('?');
  const params = new URLSearchParams(search);
  params.sort();
  return `${path}${params.toString() ? `?${params}` : ''}`;
}

const session = createSlice({
  name: 'documentDataSession',
  initialState: { generation: 0, revision: 0, recentRevision: 0, views: {} },
  reducers: {
    

    documentDataChanged(state) { state.revision += 1; },
    

    recentDocumentsChanged(state) { state.recentRevision += 1; },
    

    documentSessionCleared(state) {
      state.generation += 1;
      state.revision += 1;
      state.views = {};
    },
    

    rememberDocumentView(state, { payload: { key, value } }) {
      delete state.views[key];
      state.views[key] = value;
    },
  },
});
export const { documentDataChanged, recentDocumentsChanged, documentSessionCleared, rememberDocumentView } = session.actions;
export const documentDataSessionReducer = session.reducer;



export function documentResourceRevision(state, url) {
  return state.revision + (url.startsWith('/simul/archive/recent-documents') ? state.recentRevision : 0);
}

export const documentDataApi = createApi({
  reducerPath: 'documentData',
  keepUnusedDataFor: DOCUMENT_CACHE_SECONDS,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['Documents', 'RecentDocuments'],
  // Components retain subscriptions while visible; imperative edit flows can
  // force a fresh read through the same endpoint without a second cache.
  endpoints: (build) => ({
    resource: build.query({
      serializeQueryArgs: ({ queryArgs }) => JSON.stringify([
        queryArgs.scope, documentResourceUrl(queryArgs.url), queryArgs.pageSize || 0,
      ]),
      forceRefetch: ({ currentArg, endpointState }) => (currentArg?.pages || 1) > (endpointState?.data?.cachedPages || 1),
      async queryFn(arg, queryApi) {
        const generation = queryApi.getState().documentDataSession.generation;
        const existing = documentDataApi.endpoints.resource.select(arg)(queryApi.getState()).data;
        let mayAppend = !queryApi.forced && arg.pageSize && existing?.cachedPages
          && (arg.pages || 1) > existing.cachedPages
          && Date.now() - (existing.snapshotAt || 0) < DOCUMENT_FRESH_SECONDS * 1000;
        const pages = Math.max(arg.pages || 1, existing?.cachedPages || 1);
        // A write that finishes during a read invalidates its response. Re-read
        // before fulfilling so an older list can never win after a mutation.
        while (!queryApi.signal.aborted) {
          const revision = documentResourceRevision(queryApi.getState().documentDataSession, arg.url);
          try {
            const append = mayAppend && existing.cacheRevision === revision;
            const snapshotAt = append ? existing.snapshotAt : Date.now();
            let result = append ? existing : undefined;
            let collected = append ? [...existing.documents] : [];
            let restart = false;
            for (let page = append ? existing.cachedPages : 0; page < pages; page += 1) {
              let url = arg.url;
              if (arg.pageSize) {
                const [path, search] = url.split('?');
                const params = new URLSearchParams(search);
                params.set('first', String(page * arg.pageSize));
                url = `${path}?${params}`;
              }
              const response = await api.get(url, { signal: queryApi.signal, timeout: 15000 });
              result = response.data;
              if (arg.pageSize) {
                const incoming = result?.documents || [];
                // A concurrent insert/delete or shifted page boundary requires
                // rebuilding the snapshot, rather than mixing old/new offsets.
                const totalNow = result?.total ?? result?.total_filtered;
                const seen = new Set(collected.map((doc) => doc.document_id).filter(Boolean));
                if (append && ((totalNow != null && Number(totalNow) !== Number(existing.total))
                  || incoming.some((doc) => doc.document_id && seen.has(doc.document_id)))) {
                  mayAppend = false;
                  restart = true;
                  break;
                }
                collected = [...collected, ...incoming];
                const total = Number(result?.total ?? result?.total_filtered ?? collected.length);
                const hasNext = result?.pagination?.has_next ?? collected.length < total;
                result = { ...result, documents: collected, cachedPages: page + 1,
                  total, pagination: { ...result?.pagination, has_next: hasNext, next_first: collected.length } };
                if (!hasNext) break;
              }
            }
            if (restart) continue;
            const current = queryApi.getState().documentDataSession;
            if (current.generation !== generation) return { error: { status: 'SESSION_CHANGED' } };
            if (documentResourceRevision(current, arg.url) !== revision) continue;
            return { data: { ...result, cacheRevision: revision, snapshotAt } };
          } catch (error) {
            return { error: { status: error?.response?.status || error?.code || 'NETWORK_ERROR',
              data: error?.response?.data, message: error.message || 'Could not load documents.' } };
          }
        }
        return { error: { status: 'CANCELLED' } };
      },
      providesTags: (_result, _error, arg) => arg.url.startsWith('/simul/archive/recent-documents')
        ? ['RecentDocuments'] : ['Documents'],
    }),
  }),
});



export function clearDocumentSession(store) {
  store.dispatch(documentSessionCleared());
  store.dispatch(documentDataApi.util.resetApiState());
}



export const documentPermissionMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  if (documentDataApi.endpoints.resource.matchRejected(action) && [401, 403].includes(action.payload?.status)) {
    clearDocumentSession(store);
  }
  return result;
};



export function invalidateDocumentData(store, { recentOnly = false } = {}) {
  store.dispatch(recentOnly ? recentDocumentsChanged() : documentDataChanged());
  // RTK's default tag invalidation removes unsubscribed results. Keep those
  // snapshots for instant return navigation; their revision forces a refresh
  // when revisited. Only active subscribers need an immediate network request.
  // RTK 1.9 batches its subscription state until the end of this tick.
  return Promise.resolve().then(() => {
    const state = store.getState().documentData;
    Object.entries(state.queries).forEach(([key, query]) => {
      if (!query.originalArgs || !Object.keys(state.subscriptions[key] || {}).length) return;
      if (recentOnly && !query.originalArgs.url.startsWith('/simul/archive/recent-documents')) return;
      store.dispatch(documentDataApi.endpoints.resource.initiate(query.originalArgs, { subscribe: false, forceRefetch: true }));
    });
  });
}

// All document writers use the shared Axios instance, including dialogs and
// worker-job polling. Attach once per application store, after token refresh.


export function attachDocumentInvalidation(store) {
  if (!api.interceptors?.response) return () => {};
  const id = api.interceptors.response.use(

  (response) => {
    const { method = 'get', url = '' } = response.config || {};
    if (/^(post|put|patch|delete)$/i.test(method) &&
        /^\/(documents(?:\/|$)|document-library(?:\/|$)|simul\/archive(?:\/|$)|admin\/documents(?:\/|$))/.test(url)) {
      // Preparing a preview/download does not change the document registry.
      if (!/preview-pdf\/ensure|\/downloads(?:\/|$)/.test(url)) {
        invalidateDocumentData(store, { recentOnly: /\/recent-open$/.test(url) });
      }
    }
    // Completed asynchronous transfers/trash jobs can change data after POST.
    if (/\/(jobs|uploads)\//.test(url) && /completed|succeeded/.test(String(response.data?.job?.status || response.data?.status || ''))) {
      invalidateDocumentData(store);
    }
    return response;
  }, (error) => {
    const status = error?.response?.status;
    const path = String(error?.config?.url || '')
      .replace(/^https?:\/\/[^/]+/i, '').replace(/^\/api(?=\/)/, '');
    const documentRequest = /^\/(documents(?:\/|$)|document-library(?:\/|$)|simul\/archive(?:\/|$)|admin\/documents(?:\/|$))/.test(path);
    const accountDenied = ['employee_inactive', 'onboarding_required', 'session_revoked']
      .includes(error?.response?.data?.code);
    // Authentication loss affects every workspace. A forbidden payment review,
    // for example, says nothing about access to the matter's documents.
    if (status === 401 || (status === 403 && (documentRequest || accountDenied))) clearDocumentSession(store);
    return Promise.reject(error);
  });
  return () => api.interceptors.response.eject(id);
}
